import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  HeaderTitle,
  IconButton,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableWrapper,
} from '../../../services/settings/appStyle/global.style';
import { Loader, Table, IconButton as IconButtonWithText } from '../../../Components/uielements';
import { IntlMessages } from '../../../helper/langHelper';
import { AddPlusIcon, EditIcon, EyeIcon } from '../../../Components/icons';
import { UserContainer } from '../UserV2/user.style';
import { LoadingContent, LoadingModal } from '../EmailTemplate/emailTemplate.style';
import { getCheckList, getCheckListForListing } from '../../../services/bioplus-services/checkList';
import moment from 'moment';
import CreateDeclarationListModal from './modals/createDeclarationListModal';
import ViewCheckListModal from './modals/viewCheckListModal';
import { useUserContext } from '../../../hooks/UserContext';
import { useCompany } from '../../../hooks/CompanyContext';

// Maps the acting user's role to the list of declaration `userType`s they
// are NOT allowed to edit.
// - OWNER / EMPLOYEE: cannot edit declarations created by ADMIN or PARTNER (Distributor).
// - PARTNER (Distributor): cannot edit declarations created by ADMIN.
const NOT_ALLOW_EDIT_DECLARATION_MAP = {
  OWNER: ['ADMIN', 'PARTNER'],
  EMPLOYEE: ['ADMIN', 'PARTNER'],
  PARTNER: ['ADMIN'],
};

const DeclarationList = () => {
  const [editDeclarationListId, setEditDeclarationListId] = useState(null);
  const [viewDeclarationListId, setViewDeclarationListId] = useState(null);
  const [isCreateDeclarationList, setIsCreateDeclarationList] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const companyContext = useCompany();
  const companyFilter = companyContext?.company?._id;

  const queryClient = useQueryClient();
  const userContext = useUserContext();
  const companyId = companyFilter ?? userContext?.company?.companyId?._id;
  const userType = companyFilter
    ? 'OWNER'
    : userContext?.company?.role === 'EMPLOYEE'
    ? 'OWNER'
    : userContext?.company?.role || 'ADMIN';

  // Role based permission check for the "declaration" module
  const { userPermission } = userContext;
  const isDeclarationListAllowToManage = userPermission?.find(
    (permission) => permission?.label === 'declaration'
  );

  const canCreateDeclarationList = !!isDeclarationListAllowToManage?.isCreate;
  const canManageDeclarationList = !!isDeclarationListAllowToManage?.isManage;
  // This is used only for the default role. If the default role is EMPLOYEE, send OWNER instead.
  const declarationListData = useQuery(
    ['declarationLists', page, pageSize, userType, companyId],
    () =>
      getCheckListForListing(page, pageSize, {
        userType,
        listType: 'DECLARATION',
        companyId,
      })
  );
  const declarationListTableData = declarationListData?.data?.checklists ?? [];

  const activeDeclarationListId = editDeclarationListId || viewDeclarationListId;
  const declarationListQuery = useQuery(
    ['declarationList', activeDeclarationListId],
    () => getCheckList({ questionnaireId: activeDeclarationListId }),
    {
      enabled: !!activeDeclarationListId,
      onError: (error) => {
        alert(`Error: ${error?.response?.data?.message || 'Failed to load checklist'}`);
        setEditDeclarationListId(null);
        setViewDeclarationListId(null);
      },
    }
  );

  const handleEditCheckList = (data) => {
    queryClient.removeQueries(['declarationList', data._id]);
    setViewDeclarationListId(null); // Close view if open
    setEditDeclarationListId(data._id);
  };

  const handleViewCheckList = (checkListId) => {
    queryClient.removeQueries(['declarationList', checkListId]);
    setEditDeclarationListId(null); // Close edit if open
    setViewDeclarationListId(checkListId);
  };

  const handleCloseEditModal = () => {
    setEditDeclarationListId(null);
  };

  const handleCloseViewModal = () => {
    setViewDeclarationListId(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateDeclarationList(false);
  };

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };

  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  const groupColumns = useMemo(
    () => [
      {
        Header: (
          <IntlMessages id="table.col.declaration.title" defaultMessage="Declaration List Title" />
        ),
        accessor: 'title',
        width: '45%',
      },

      {
        Header: <IntlMessages id="table.col.createdAt" defaultMessage="Created At" />,
        accessor: 'createdAt',
        width: '40%',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },

      {
        Header: <IntlMessages id="table.col.actions" defaultMessage="Actions" />,
        accessor: 'actions',
        isAction: true,
        width: '15%',
      },
    ],
    []
  );

  // Whether the current user is blocked from editing a given declaration,
  // based on their role and the declaration's userType.
  const isEditDeclarationDisabled = (data) => {
    const blockedUserTypes = NOT_ALLOW_EDIT_DECLARATION_MAP[userContext?.company?.role] || [];
    return blockedUserTypes.includes(data?.userType);
  };

  const actionList = [
    ...(canManageDeclarationList
      ? [
          (data) => (
            <IconButton
              onClick={() => handleEditCheckList(data)}
              disabled={isEditDeclarationDisabled(data)}
              style={
                isEditDeclarationDisabled(data)
                  ? { opacity: 0.4, cursor: 'not-allowed' }
                  : undefined
              }
            >
              <EditIcon style={{ width: '22px', height: '22px' }} />
            </IconButton>
          ),
        ]
      : []),
    (data) => (
      <IconButton onClick={() => handleViewCheckList(data?._id)}>
        <EyeIcon style={{ width: '22px', height: '22px' }} />
      </IconButton>
    ),
  ];

  if (declarationListData.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <>
      <StyledContainer>
        <UserContainer>
          <HeaderTitle>
            <IntlMessages id="module.declaration.list" />
          </HeaderTitle>

          {canCreateDeclarationList && (
            <IconButtonWithText
              text={<IntlMessages id="create.declaration.list" />}
              icon={<AddPlusIcon />}
              onClick={() => {
                setIsCreateDeclarationList(true);
              }}
            />
          )}
        </UserContainer>
        <TableWrapper>
          <Table
            data={declarationListTableData}
            columns={groupColumns}
            actions={actionList}
            hasFooter={true}
            page={page - 1}
            pageSize={pageSize}
            prevPage={handlePreviousPage}
            nextPage={handleNextPage}
            total={declarationListData?.data?.checklists?.length || 0}
          />
        </TableWrapper>
      </StyledContainer>

      {declarationListQuery.isLoading && activeDeclarationListId && (
        <LoadingModal>
          <LoadingContent>
            <Loader loadingText="Loading declaration data..." />
          </LoadingContent>
        </LoadingModal>
      )}
      {/* Modals */}
      {isCreateDeclarationList && <CreateDeclarationListModal onClose={handleCloseCreateModal} />}

      {editDeclarationListId && declarationListQuery?.data && !declarationListQuery.isLoading && (
        <CreateDeclarationListModal
          onClose={handleCloseEditModal}
          data={declarationListQuery?.data?.checklist}
          actionEditOrCreate="edit"
          checkListId={editDeclarationListId}
        />
      )}

      {viewDeclarationListId && declarationListQuery?.data && !declarationListQuery.isLoading && (
        <ViewCheckListModal
          data={declarationListQuery?.data?.checklist}
          onClose={handleCloseViewModal}
        />
      )}
    </>
  );
};

export default DeclarationList;
